import React, {useEffect} from 'react';
import {useSelector, useDispatch} from 'react-redux';
import {Box, Typography, IconButton, List, ListItemButton, ListItemText, Button} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import {setHistoryPanelOpen, setActiveThreadId, loadThread, removeThreadFromState, startNewChat} from './chatSlice';
import {fetchInsightsThreadsList} from './fetchInsightsThreads';

const PANEL_WIDTH = 320;
const THREAD_KEY = 'chat_thread_id';

/**
 * Slide-out panel from the right listing conversation threads; load, delete, new chat.
 * Threads are persisted in localStorage per customer.
 * @param {Object} props
 * @param {boolean} props.open - Whether the panel is visible.
 * @param {function(): void} [props.onClose] - Called when panel closes (e.g. overlay click).
 * @return {JSX.Element}
 */
function ChatHistoryPanel({open, onClose, onThreadLoaded}) {
  const dispatch = useDispatch();
  const {threads, activeThreadId, historyPanelOpen} = useSelector((state) => {
    return state.chat;
  });
  const isOpen = open ?? historyPanelOpen;

  useEffect(() => {
    if (!isOpen) return;
    fetchInsightsThreadsList(dispatch);
  }, [isOpen, dispatch]);

  const handleClose = () => {
    dispatch(setHistoryPanelOpen(false));
    onClose?.();
  };

  const handleLoadThread = async (threadId) => {
    try {
      const response = await fetch(`/insights/thread/${encodeURIComponent(threadId)}/history`, {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch thread history');
      }

      const data = await response.json();

      dispatch(
        loadThread({
          threadId: data.threadId,
          chatHistory: data.chatHistory,
        })
      );

      sessionStorage.setItem(THREAD_KEY, data.threadId);
      onThreadLoaded?.(data.threadId);
      handleClose();
    } catch (error) {
      console.error('Failed to load thread:', error);
    }
  };

  const handleRemoveThread = async (e, threadId) => {
    e.stopPropagation();

    try {
      const response = await fetch(`/insights/thread/${encodeURIComponent(threadId)}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to delete thread');
      }

      await response.json();
      dispatch(removeThreadFromState(threadId));
    } catch (error) {
      console.error('Failed to delete thread:', error);
    }
  };

  const handleNewChat = () => {
    // Generate a new threadId for the new chat
    const newThreadId = Date.now().toString();
    sessionStorage.setItem(THREAD_KEY, newThreadId);
    dispatch(startNewChat());
    dispatch(setActiveThreadId(newThreadId));
    onThreadLoaded?.(newThreadId);
    handleClose();
  };

  if (!isOpen) return null;

  return (
    <>
      <Box
        onClick={handleClose}
        sx={{
          position: 'fixed',
          left: 0,
          top: 0,
          bottom: 0,
          right: 0,
          backgroundColor: 'rgba(0,0,0,0.3)',
          zIndex: 1310,
          opacity: isOpen ? 1 : 0,
          visibility: isOpen ? 'visible' : 'hidden',
          transition: 'opacity 0.2s, visibility 0.2s',
        }}
        aria-hidden='true'
      />
      <Box
        sx={{
          position: 'fixed',
          right: 0,
          top: 0,
          bottom: 0,
          width: PANEL_WIDTH,
          maxWidth: '90vw',
          backgroundColor: '#fff',
          boxShadow: '-4px 0 12px rgba(0,0,0,0.1)',
          zIndex: 1311,
          display: 'flex',
          flexDirection: 'column',
          transform: isOpen ? 'translateX(0)' : 'translateX(100%)',
          transition: 'transform 0.25s ease-out',
        }}
        role='dialog'
        aria-label='Conversation history'
      >
        <Box
          sx={{
            p: 2,
            borderBottom: '1px solid #e0e0e0',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            backgroundColor: '#f5f5f5',
          }}
        >
          <Box sx={{display: 'flex', alignItems: 'center', gap: 1}}>
            <HistoryIcon color='primary' />
            <Typography variant='h6'>History</Typography>
          </Box>
          <IconButton size='small' onClick={handleClose} aria-label='Close history' sx={{color: '#666'}}>
            <CloseIcon />
          </IconButton>
        </Box>

        <Box sx={{p: 1.5, borderBottom: '1px solid #e0e0e0'}}>
          <Button fullWidth variant='contained' color='primary' onClick={handleNewChat} sx={{textTransform: 'none'}}>
            New chat
          </Button>
        </Box>

        <Box sx={{flex: 1, overflowY: 'auto'}}>
          {threads.length === 0 ? (
            <Typography variant='body2' color='text.secondary' sx={{p: 2, textAlign: 'center'}}>
              No past conversations. Start a chat and it will appear here.
            </Typography>
          ) : (
            <List disablePadding>
              {threads.map((thread) => {
                return (
                  <ListItemButton
                    key={thread.id}
                    selected={activeThreadId === thread.id}
                    onClick={() => {
                      handleLoadThread(thread.id);
                    }}
                    sx={{
                      py: 1.5,
                      '& .thread-delete': {opacity: 0},
                      '&:hover .thread-delete': {opacity: 1},
                    }}
                  >
                    <ListItemText
                      primary={thread.title}
                      secondary={new Date(thread.updatedAt).toLocaleDateString(undefined, {
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                      primaryTypographyProps={{
                        noWrap: true,
                        title: thread.title,
                      }}
                    />
                    <IconButton
                      size='small'
                      className='thread-delete'
                      aria-label={`Delete ${thread.title}`}
                      onClick={(e) => {
                        handleRemoveThread(e, thread.id);
                      }}
                      sx={{ml: 0.5, color: 'text.secondary'}}
                    >
                      <DeleteOutlineIcon fontSize='small' />
                    </IconButton>
                  </ListItemButton>
                );
              })}
            </List>
          )}
        </Box>
      </Box>
    </>
  );
}

export default ChatHistoryPanel;
